import {
    Observable,
    Subject,
    Subscription,
    timer,
    concat,
    ReplaySubject,
} from "rxjs";
import { Injectable, OnDestroy } from "@angular/core";
import { environment } from "../../environments/environment";
import { AuthData } from "../models/AuthData";
import { Moment, utc, isMoment } from "moment-timezone";
import { CacheService } from "./cache.service";
import { Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { map, mapTo, tap } from "rxjs/operators";
import { decode } from "js-base64";
import pako from "pako";
import { AuthService as AuthServiceSSO } from "@auth0/auth0-angular";
import { jwtDecode } from "jwt-decode";
import moment from "moment";

export interface AuthResponse {
    token_type?: string;
    access_token?: string;
    expires_in: number;
    refresh_token?: string;
    mfa_token?: string;
}
export enum AUTH_ERROR_CODES {
    UNKNOWN_ERROR,
    NOT_AUTHENTICATED,
    TIMED_OUT,
    TOKEN_EXPIRED,
}
export interface LoginResult {
    status: "SUCCESS" | "MFA_REQUIRED";
    mfaToken?: string;
}

export const AUTH_ERROR_MESSAGES = new Map<AUTH_ERROR_CODES, string>([
    [AUTH_ERROR_CODES.UNKNOWN_ERROR, "Unknown Error"],
    [AUTH_ERROR_CODES.NOT_AUTHENTICATED, "Not authenticated."],
    [AUTH_ERROR_CODES.TIMED_OUT, "Timed out."],
    [AUTH_ERROR_CODES.TOKEN_EXPIRED, "Auth Token hasItem Expired."],
]);

export class AuthError extends Error {
    errorCode: AUTH_ERROR_CODES;

    constructor(code: AUTH_ERROR_CODES, message?: string) {
        super(
            "Auth Error with code: " +
                code.toString() +
                ": " +
                AUTH_ERROR_MESSAGES.get(code) +
                (message ? " (" + message + ")" : "")
        );

        this.errorCode = code;
    }
}

export const CONFIG_TIMEOUT_MINUTES = 30;

@Injectable()
export class AuthService implements OnDestroy {
    public _authenticated: boolean;
    private _tokenRefreshSubscription: Subscription;
    private _authData: AuthData;
    private _timeOutTime: Moment;
    private _timeOutSubscription: Subscription;
    private _timeout$: Subject<true> = new Subject<true>();
    private _authToken$: ReplaySubject<string> = new ReplaySubject<string>(1);
    private _isTimedOut: boolean = false;
    private isSSOFlow: boolean = localStorage.getItem('isSSOFlow') === 'true';

    get timeout$(): Observable<true> {
        return this._timeout$.asObservable();
    }

    get authToken$(): Observable<string> {
        return this._authToken$.asObservable();
    }

    private static getAuthDataFromStorage(): AuthData {
        const storageData = localStorage.getItem("authData");
        return JSON.parse(storageData) || null;
    }

    public parseToken(token: string): string {
        return JSON.parse(
            decode(token.split(".")[1].replace("-", "+").replace("_", "/"))
        );
    }

    public getUserClaims(): Record<string, any> {
        const b64Data = this._authData.tokenData.userClaims;
        const strData = atob(b64Data);
        const charData = strData.split("").map((x) => x.charCodeAt(0));
        const binData = new Uint8Array(charData);
        const result = pako.inflate(binData, { to: "string" });

        return JSON.parse(result);
    }

    private static getTimeOutFromStorage(): Moment {
        const timeOutString = localStorage.getItem("timeOut");

        if (timeOutString) {
            return utc(timeOutString) || null;
        } else {
            return null;
        }
    }

    private static checkTimeout(time: Moment): void {
        if (!time || !isMoment(time) || utc().isAfter(time)) {
            throw new AuthError(AUTH_ERROR_CODES.TIMED_OUT);
        }
    }

    private static checkAuthData(authData: AuthData): void {
        if (!authData || !authData.expires_at) {
            throw new AuthError(AUTH_ERROR_CODES.NOT_AUTHENTICATED);
        }

        if (utc(authData.expires_at).diff(utc(), "seconds") < 15) {
            throw new AuthError(AUTH_ERROR_CODES.TOKEN_EXPIRED);
        }
    }

    constructor(
        private http: HttpClient,
        private cacheService: CacheService,
        private router: Router,
        private auth0: AuthServiceSSO,
    ) {
        this._authData = AuthService.getAuthDataFromStorage();
        this._timeOutTime = AuthService.getTimeOutFromStorage();

        if (this._authData && this._timeOutTime && !this.isSSOFlow) {
            try {
                if (this.isAuthenticated()) {
                    this.startTokenRefreshTimer(false);
                    this.setTimeout(CONFIG_TIMEOUT_MINUTES);
                }
            } catch (e) {
                this.logout();
            }
        } else if (this.isSSOFlow) {
            this.auth0.idTokenClaims$.subscribe((claims) => {
                const sessionExpiresAt = moment(claims.session_expires_at).format()
                const timeNow = moment().format();
                this.setTimeout(moment(sessionExpiresAt).diff(moment(timeNow), 'second')/60);
            })
        }
    }

    private handleAuthResponse(response: AuthResponse) {
        const authData: AuthData = {
            token: response.access_token,
            expires_at: utc().add({ seconds: +response.expires_in }).format(),
            refresh_token: response.refresh_token,
            tokenData: this.parseToken(response.access_token),
        };

        AuthService.checkAuthData(authData);

        this.setAuthData(authData);
        this.setTimeout(CONFIG_TIMEOUT_MINUTES);

        this._authToken$.next(authData.token);

        this.startTokenRefreshTimer(false);
    }

    /**
     * Logs the user in using a username and password.
     * @param username
     * @param password
     * @returns {Promise<void>} A promise which either resolves or rejects, depending on if the log-in was
     * successful or not.
     */

    login(username: string, password: string): Observable<LoginResult> {
        const body = `grant_type=password&username=${encodeURIComponent(
            username
        )}&password=${encodeURIComponent(password)}&client_id=AccountAdmin`;
        const headers = new HttpHeaders({
            "Content-Type": "application/x-www-form-urlencoded",
        });

        return this.http
            .post<AuthResponse>(environment.authTokenPath, body, {
                headers: headers,
            })
            .pipe(
                map((response: AuthResponse) => {
                    const tokenType = response.token_type.toLowerCase();
                    if (tokenType == "bearer") {
                        this.handleAuthResponse(response);

                        return {
                            status: "SUCCESS",
                        };
                    } else if (tokenType === "mfa") {
                        return {
                            status: "MFA_REQUIRED",
                            mfaToken: response.mfa_token,
                        };
                    }
                })
            );
    }

    loginWithMfa(mfaToken: string, mfaCode: string): Observable<LoginResult> {
        const body = `grant_type=mfa&mfa_token=${encodeURIComponent(
            mfaToken
        )}&mfa_code=${encodeURIComponent(mfaCode)}&client_id=AccountAdmin`;
        const headers = new HttpHeaders({
            "Content-Type": "application/x-www-form-urlencoded",
        });

        return this.http
            .post<AuthResponse>(environment.authTokenPath, body, {
                headers: headers,
            })
            .pipe(
                map((response: AuthResponse) => {
                    const tokenType = response.token_type.toLowerCase();
                    if (tokenType == "bearer") {
                        this.handleAuthResponse(response);
                        return {
                            status: "SUCCESS",
                        };
                    }
                })
            );
    }

    // SSO Flow
    loginWithSSO() {
        this.auth0.loginWithRedirect({
            authorizationParams: {
                audience: "controlant",
                redirect_uri: window.location.origin,
            },
        });
    }

    // SSO Flow
    handleAuth0Token(token: string) {
        try {
            const accessToken = jwtDecode(token);
            if (accessToken) {
                const expires_at = moment
                    .unix(accessToken.exp)
                    .utc()
                    .format("YYYY-MM-DDTHH:mm:ss[Z]");
                const authData: AuthData = {
                    token: token,
                    expires_at,
                    tokenData: this.parseToken(token),
                };
                this.setAuthData(authData);
            }
        } catch (error) {
            alert(`Error decoding token: ${error.message}`);
        }
    }

    /**
     * Logs the user out and clears the local storage of any session information.
     */
    logout(): void {
        if (this.isSSOFlow && !this._isTimedOut) {
            this.auth0.logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            });

            localStorage.setItem("isSSOFlow", "false");
            this.clearSessionData();
        } else {
            this.clearSessionData();
        }
    }

    clearSessionData(): void {
        if (this._tokenRefreshSubscription) {
            this._tokenRefreshSubscription.unsubscribe();
        }

        if (this._timeOutSubscription) {
            this._timeOutSubscription.unsubscribe();
        }

        this._authData = null;
        this._timeOutTime = null;

        localStorage.removeItem("authData");
        localStorage.removeItem("timeOut");
        localStorage.removeItem("isMultiOrg");

        this.cacheService.clearCache();
    }

    /**
     * Checks if user is authenticated or not. Internally runs checks for auth data in local storage as well as if
     * time out hasItem been triggered.
     * @throws {AuthError} Throws an AUTH_* error on the first call that would otherwise result in false.
     * @returns {boolean} Returns true if authenticated, false otherwise. First call that results in a false will throw
     * an AuthError rather than returning a value; following calls will then simply return false.
     */
    isAuthenticated(): boolean {
        try {
            AuthService.checkAuthData(this._authData);
            AuthService.checkTimeout(this._timeOutTime);
        } catch (e) {
            this._authenticated = false;
            throw e;
        }

        return (this._authenticated = true);
    }

    /**
     * Refreshes the timeout timer for the user, but only if it has been set already (which is done upon successful
     * login).
     * @returns {boolean} Whether setting the timeout was successful (fails if not initialized by login() earlier)
     */
    refreshTimeout(): boolean {
        AuthService.checkTimeout(this._timeOutTime);

        this.setTimeout(CONFIG_TIMEOUT_MINUTES);

        return true;
    }

    /**
     * Returns the token currently in memory, for use with the controlant API.
     * @returns {any}
     */
    getToken(): string {
        if (
            this._authData === null ||
            typeof this._authData.token !== "string"
        ) {
            return "";
        }

        return this._authData.token;
    }

    userUsesClaims(): boolean {
        return this._authData.tokenData && this._authData.tokenData !== null
            ? this._authData.tokenData.userUsesClaims === "true"
            : false;
    }

    public setTimeout(minutes: number): void {
        this._timeOutTime = utc().clone().add({ minutes: minutes });

        if (this._timeOutSubscription) {
            this._timeOutSubscription.unsubscribe();
        }
        console.log("Setting timeout for ", this._timeOutTime.format());

        this._timeOutSubscription = timer(minutes * 60 * 1000).subscribe(() => {
            this._isTimedOut = true;
            this._timeout$.next(true);
            console.log("Timed out");
            this.logout();
            this.router.navigate([
                "/login",
                { errorCode: AUTH_ERROR_CODES.TIMED_OUT, url: this.router.url },
            ]);
        });

        localStorage.setItem("timeOut", this._timeOutTime.format());
    }

    clearToken(): void {
        this.logout();
        this.router.navigate([
            "/login",
            {
                errorCode: AUTH_ERROR_CODES.NOT_AUTHENTICATED,
                url: this.router.url,
            },
        ]);
    }

    public setAuthData(data: AuthData): void {
        this._authData = Object.assign(this._authData || {}, data);

        return localStorage.setItem("authData", JSON.stringify(this._authData));
    }

    startTokenRefreshTimer(startNow: boolean, buffer = 10): void {
        if (this._authData !== null) {
            if (this._tokenRefreshSubscription) {
                this._tokenRefreshSubscription.unsubscribe();
            }

            const timeUntilTokenExpires = utc(this._authData.expires_at).diff(
                utc(),
                "seconds"
            );

            this._tokenRefreshSubscription = concat(
                timer(startNow ? 0 : (timeUntilTokenExpires - buffer) * 1000),
                this.refreshToken()
            ).subscribe({
                next: () => undefined,
                error: (error) => console.error("Stream Error: %s", error),
                complete: () => {
                    console.log("Token was auto-refreshed.");

                    this.startTokenRefreshTimer(false, buffer);
                },
            });
        }
    }

    private refreshToken(): Observable<boolean> {
        const headers = new HttpHeaders({
            "Content-Type": "application/x-www-form-urlencoded",
        });

        return this.http
            .post<AuthResponse>(
                environment.authTokenPath,
                "grant_type=refresh_token&refresh_token=" +
                    this._authData.refresh_token +
                    "&client_id=AccountAdmin&client_secret=z7NTmRT61IXFF9VuZMVP",
                { headers: headers }
            )
            .pipe(
                tap((response: AuthResponse) => {
                    const authData: AuthData = {
                        token: response.access_token,
                        expires_at: utc()
                            .add({ seconds: response.expires_in })
                            .format(),
                        refresh_token: response.refresh_token,
                        tokenData: this.parseToken(response.access_token),
                    };

                    this.setAuthData(authData);
                }),
                mapTo(true)
            );
    }

    ngOnDestroy() {
        if (this._tokenRefreshSubscription) {
            this._tokenRefreshSubscription.unsubscribe();
        }

        if (this._timeOutSubscription) {
            this._timeOutSubscription.unsubscribe();
        }
    }
}
